import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Layout from '../components/Layout';
import api, { couponsAPI } from '../services/api';
import { useToast } from '../components/Toast';
import {
  Search, Plus, Edit2, Trash2, RefreshCw, X,
  Percent, ToggleLeft, ToggleRight, Send,
} from 'lucide-react';

const STATUS_OPTS = ['ALL', 'ACTIVE', 'INACTIVE', 'EXPIRED'];

const NOTIFY_TITLE_MAX   = 65;
const NOTIFY_MESSAGE_MAX = 500;

// ── Helpers ──────────────────────────────────────────────────────────────
function isExpired(coupon) {
  if (!coupon.expiresAt) return false;
  return new Date(coupon.expiresAt).getTime() < Date.now();
}

function statusOf(coupon) {
  if (isExpired(coupon)) return 'EXPIRED';
  return coupon.isActive ? 'ACTIVE' : 'INACTIVE';
}

function formatDate(dt) {
  if (!dt) return '—';
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function formatDiscount(coupon) {
  if (coupon.discountType === 'FLAT') return `₹${coupon.discountValue} OFF`;
  const cap = coupon.maximumDiscount ? ` (up to ₹${coupon.maximumDiscount})` : '';
  return `${coupon.discountValue}% OFF${cap}`;
}

// datetime-local input <-> backend LocalDateTime ("yyyy-MM-ddTHH:mm:ss")
function toDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toBackendIso(datetimeLocalValue) {
  if (!datetimeLocalValue) return null;
  // "2026-12-31T23:59" -> "2026-12-31T23:59:00"
  return datetimeLocalValue.length === 16 ? `${datetimeLocalValue}:00` : datetimeLocalValue;
}

// ── CouponModal ───────────────────────────────────────────────────────────
function CouponModal({ coupon, existingCoupons, onSave, onClose, loading }) {
  const isEdit = !!coupon;

  const [code, setCode] = useState(coupon?.code || '');
  const [description, setDescription] = useState(coupon?.description || '');
  const [discountType, setDiscountType] = useState(coupon?.discountType || 'PERCENTAGE');
  const [discountValue, setDiscountValue] = useState(coupon?.discountValue ?? '');
  const [minimumOrderAmount, setMinimumOrderAmount] = useState(coupon?.minimumOrderAmount ?? '');
  const [maximumDiscount, setMaximumDiscount] = useState(coupon?.maximumDiscount ?? '');
  const [isActive, setIsActive] = useState(coupon?.isActive ?? true);
  const [expiresAt, setExpiresAt] = useState(toDatetimeLocalValue(coupon?.expiresAt));
  const [touched, setTouched] = useState({ code: false, discountValue: false });

  const codeTrimmed = code.trim().toUpperCase();

  const isDuplicate = existingCoupons.some(
    c => (!isEdit || c.id !== coupon.id) && c.code.trim().toUpperCase() === codeTrimmed
  );

  const discountValueNum = discountValue === '' ? NaN : Number(discountValue);

  const getCodeError = () => {
    if (!codeTrimmed) return 'Coupon code is required';
    if (isDuplicate) return `Code "${codeTrimmed}" already exists`;
    return '';
  };

  const getDiscountValueError = () => {
    if (discountValue === '' || Number.isNaN(discountValueNum)) return 'Discount value is required';
    if (discountValueNum <= 0) return 'Must be greater than 0';
    if (discountType === 'PERCENTAGE' && discountValueNum > 100) return 'Percentage cannot exceed 100';
    return '';
  };

  const errors = {
    code: getCodeError(),
    discountValue: getDiscountValueError(),
  };
  const isValid = !errors.code && !errors.discountValue;

  const touchAll = () => setTouched({ code: true, discountValue: true });

  const handleSubmit = (e) => {
    e.preventDefault();
    touchAll();
    if (!isValid) return;

    onSave({
      code: codeTrimmed,
      description: description.trim() || null,
      discountType,
      discountValue: discountValueNum,
      minimumOrderAmount: minimumOrderAmount === '' ? null : Number(minimumOrderAmount),
      maximumDiscount: maximumDiscount === '' ? null : Number(maximumDiscount),
      isActive,
      expiresAt: toBackendIso(expiresAt),
      // Coupons no longer push notifications to customers (order updates do).
      notifyCustomers: false,
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? 'Edit Coupon' : 'New Coupon'}</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="cp-code">Coupon Code <span style={{ color: 'var(--red)' }}>*</span></label>
            <input
              id="cp-code"
              value={code}
              onChange={e => { setCode(e.target.value); setTouched(t => ({ ...t, code: true })); }}
              onBlur={() => setTouched(t => ({ ...t, code: true }))}
              placeholder="e.g. SAVE10"
              style={{ textTransform: 'uppercase', ...(touched.code && errors.code ? { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(239,68,68,0.12)' } : {}) }}
              autoFocus
            />
            {touched.code && errors.code && (
              <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 5 }}>⚠ {errors.code}</p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="cp-desc">Description</label>
            <input
              id="cp-desc"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="e.g. Get 10% off on orders above ₹300"
            />
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="cp-type">Discount Type</label>
              <select id="cp-type" value={discountType} onChange={e => setDiscountType(e.target.value)}>
                <option value="PERCENTAGE">Percentage (%)</option>
                <option value="FLAT">Flat (₹)</option>
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="cp-value">
                Discount Value <span style={{ color: 'var(--red)' }}>*</span>
              </label>
              <input
                id="cp-value"
                type="number"
                min="0"
                step="0.01"
                value={discountValue}
                onChange={e => { setDiscountValue(e.target.value); setTouched(t => ({ ...t, discountValue: true })); }}
                onBlur={() => setTouched(t => ({ ...t, discountValue: true }))}
                placeholder={discountType === 'PERCENTAGE' ? 'e.g. 10' : 'e.g. 50'}
                style={touched.discountValue && errors.discountValue ? { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(239,68,68,0.12)' } : {}}
              />
              {touched.discountValue && errors.discountValue && (
                <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 5 }}>⚠ {errors.discountValue}</p>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="cp-min">Minimum Order Amount</label>
              <input
                id="cp-min"
                type="number"
                min="0"
                step="0.01"
                value={minimumOrderAmount}
                onChange={e => setMinimumOrderAmount(e.target.value)}
                placeholder="e.g. 300"
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="cp-max">
                Max Discount Cap {discountType !== 'PERCENTAGE' && <span style={{ color: 'var(--text-muted)' }}>(N/A)</span>}
              </label>
              <input
                id="cp-max"
                type="number"
                min="0"
                step="0.01"
                value={maximumDiscount}
                onChange={e => setMaximumDiscount(e.target.value)}
                placeholder="e.g. 100"
                disabled={discountType !== 'PERCENTAGE'}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="cp-expiry">Expires At (optional)</label>
            <input
              id="cp-expiry"
              type="datetime-local"
              value={expiresAt}
              onChange={e => setExpiresAt(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="cp-active" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                id="cp-active"
                type="checkbox"
                checked={isActive}
                onChange={e => setIsActive(e.target.checked)}
                style={{ width: 16, height: 16 }}
              />
              Active
            </label>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading} onClick={touchAll}>
              {(() => {
                if (loading) return 'Saving...';
                return isEdit ? 'Save Changes' : 'Create Coupon';
              })()}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

CouponModal.propTypes = {
  coupon: PropTypes.shape({
    id: PropTypes.string,
    code: PropTypes.string,
    description: PropTypes.string,
    discountType: PropTypes.string,
    discountValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    minimumOrderAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    maximumDiscount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isActive: PropTypes.bool,
    expiresAt: PropTypes.string,
  }),
  existingCoupons: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    code: PropTypes.string,
  })).isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

CouponModal.defaultProps = {
  coupon: null,
};

// ── NotificationModal ─────────────────────────────────────────────────────
// Admin writes a message; it is broadcast as a push notification to every
// registered customer device (POST /admin/notifications/broadcast).
function NotificationModal({ onSend, onClose, loading }) {
  const [title, setTitle]     = useState('');
  const [message, setMessage] = useState('');
  const [touched, setTouched] = useState(false);

  // Optional: link the notification to a product (tapping it opens that product in the app)
  const [products, setProducts]         = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [linked, setLinked]             = useState(null);   // { id, name }

  useEffect(() => {
    let cancelled = false;
    api.get('/admin/products', { params: { page: 0, size: 1000, status: 'ACTIVE' } })
      .then(res => { if (!cancelled) setProducts(res.data?.content || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const q = productSearch.trim().toLowerCase();
  const productMatches = q
    ? products
        .filter(p => (p.name || '').toLowerCase().includes(q)
                  || (p.sellerBusinessName || '').toLowerCase().includes(q))
        .slice(0, 8)
    : [];

  const pickProduct = (p) => {
    setLinked({ id: p.id || p.productId, name: p.name });
    setProductSearch('');
  };

  const messageTrimmed = message.trim();
  const messageError   = messageTrimmed ? '' : 'Notification message is required';

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (messageError) return;
    onSend({ title: title.trim(), message: messageTrimmed, productId: linked?.id || null });
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <span className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Send size={16} /> Send Notification
          </span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#15803d' }}>
          This message is sent as a push notification to <strong>all customers</strong> who have the app installed.
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="nt-title">Title (optional)</label>
            <input
              id="nt-title"
              value={title}
              maxLength={NOTIFY_TITLE_MAX}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Weekend special 🎉  (default: HomeMade)"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="nt-message">Message <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea
              id="nt-message"
              rows={4}
              value={message}
              maxLength={NOTIFY_MESSAGE_MAX}
              onChange={e => { setMessage(e.target.value); setTouched(true); }}
              onBlur={() => setTouched(true)}
              placeholder="e.g. Get 10% off on all orders above ₹300 this weekend. Use code SAVE10!"
              style={touched && messageError ? { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(239,68,68,0.12)' } : {}}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
              {touched && messageError
                ? <p style={{ color: 'var(--red)', fontSize: 12, margin: 0 }}>⚠ {messageError}</p>
                : <span />}
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{message.length}/{NOTIFY_MESSAGE_MAX}</span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="nt-product">Open product when tapped (optional)</label>
            {linked ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px', border: '1px solid #86efac', background: '#f0fdf4', borderRadius: 8, fontSize: 13 }}>
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  🛍️ <strong>{linked.name}</strong>
                </span>
                <button type="button" className="btn btn-ghost btn-icon btn-sm" title="Remove product link" onClick={() => setLinked(null)}>
                  <X size={14} />
                </button>
              </div>
            ) : (
              <>
                <input
                  id="nt-product"
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  placeholder="Search an active product by name or seller..."
                  autoComplete="off"
                />
                {productMatches.length > 0 && (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginTop: 4, maxHeight: 190, overflowY: 'auto', background: 'var(--bg-card)' }}>
                    {productMatches.map(p => (
                      <button
                        key={p.id || p.productId}
                        type="button"
                        onClick={() => pickProduct(p)}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', borderBottom: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}
                      >
                        <strong>{p.name}</strong>
                        <span style={{ color: 'var(--text-muted)', fontSize: 11, marginLeft: 6 }}>{p.sellerBusinessName || ''}</span>
                      </button>
                    ))}
                  </div>
                )}
                {q && productMatches.length === 0 && (
                  <small style={{ color: 'var(--text-muted)', fontSize: 11 }}>No active product matches "{productSearch}".</small>
                )}
              </>
            )}
            <small style={{ color: 'var(--text-muted)', fontSize: 11, display: 'block', marginTop: 4 }}>
              Leave empty to just show the message when the notification is tapped.
            </small>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !messageTrimmed} style={{ gap: 6 }}>
              <Send size={14} />{loading ? 'Sending...' : 'Send to All'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

NotificationModal.propTypes = {
  onSend: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

// ── DeleteModal ───────────────────────────────────────────────────────────
function DeleteModal({ coupon, onConfirm, onClose, loading }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <span className="modal-title">Delete Coupon</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ padding: '8px 0 20px' }}>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Are you sure you want to delete{' '}
            <strong style={{ color: 'var(--text-primary)' }}>&quot;{coupon.code}&quot;</strong>? This cannot be undone.
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={loading}>
            {loading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

DeleteModal.propTypes = {
  coupon: PropTypes.shape({ code: PropTypes.string }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

// ── Main Coupons Page ───────────────────────────────────────────────────────
export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [modalState, setModalState] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await couponsAPI.getAll();
      setCoupons(res.data || []);
    } catch {
      toast('Failed to load coupons', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (payload) => {
    setActionLoading(true);
    try {
      if (modalState.mode === 'create') {
        await couponsAPI.create(payload);
        toast('Coupon created successfully', 'success');
      } else {
        await couponsAPI.update(modalState.coupon.id, payload);
        toast('Coupon updated successfully', 'success');
      }
      setModalState(null);
      load();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || `Failed to ${modalState.mode} coupon`;
      toast(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await couponsAPI.delete(modalState.coupon.id);
      toast(`"${modalState.coupon.code}" deleted`, 'info');
      setModalState(null);
      load();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to delete coupon';
      toast(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleStatus = async (coupon) => {
    setTogglingId(coupon.id);
    const newActive = !coupon.isActive;
    try {
      const res = await couponsAPI.setActive(coupon.id, newActive);
      const updated = res.data;
      toast(
        newActive
          ? `"${coupon.code}" activated`
          : `"${coupon.code}" deactivated`,
        'success'
      );
      setCoupons(prev => prev.map(c => (c.id === coupon.id ? updated : c)));
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to update status';
      toast(msg, 'error');
    } finally {
      setTogglingId(null);
    }
  };

  // Broadcast a free-text push notification to every registered device
  const handleSendNotification = async ({ title, message, productId }) => {
    setActionLoading(true);
    try {
      const res = await api.post('/admin/notifications/broadcast', { title, message, productId });
      const count = res.data?.devicesTargeted ?? 0;
      if (count > 0) {
        toast(`Notification sent to ${count} device${count === 1 ? '' : 's'}`, 'success');
        setModalState(null);
      } else {
        toast('No devices are registered yet, so nothing was sent', 'info');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to send notification';
      toast(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = coupons.filter(c => {
    const q = search.toLowerCase();
    const matchesSearch = !q
      || c.code?.toLowerCase().includes(q)
      || c.description?.toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'ALL' || statusOf(c) === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const activeCount = coupons.filter(c => statusOf(c) === 'ACTIVE').length;
  const inactiveCount = coupons.filter(c => statusOf(c) === 'INACTIVE').length;
  const expiredCount = coupons.filter(c => statusOf(c) === 'EXPIRED').length;

  return (
    <Layout title="Coupons" subtitle="Create and manage discount offers shown in customer cart">
      <div className="cat-topbar">
        <div className="cat-topbar-row1">
          <div className="search-box cat-search">
            <Search size={14} className="search-icon" />
            <input placeholder="Search by code or description..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={13} /></button>
          <button className="btn btn-ghost btn-sm" onClick={() => setModalState({ mode: 'notify' })} style={{ gap: 6 }}>
            <Send size={14} /> Send Notification
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setModalState({ mode: 'create' })}>
            <Plus size={15} /> New Coupon
          </button>
        </div>
        <div className="chip-group cat-chips" style={{ margin: 0 }}>
          {STATUS_OPTS.map(s => (
            <button key={s} className={`chip ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Total', value: coupons.length, color: 'var(--text-primary)' },
          { label: 'Active', value: activeCount, color: 'var(--green)' },
          { label: 'Inactive', value: inactiveCount, color: 'var(--text-muted)' },
          { label: 'Expired', value: expiredCount, color: 'var(--red)' },
        ].map(s => (
          <div key={s.label} style={{ padding: '10px 18px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.label}</span>
          </div>
        ))}
      </div>

      {loading && (
        <div className="page-loader"><div className="spinner" /></div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="empty-state" style={{ marginTop: 60 }}>
          <Percent size={48} />
          <h3>No coupons found</h3>
          <p>{search ? 'Try a different search term' : 'Create your first coupon offer to get started'}</p>
          {!search && (
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setModalState({ mode: 'create' })}>
              <Plus size={14} /> Create Coupon
            </button>
          )}
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
                <th>Discount</th>
                <th>Min Order</th>
                <th>Expires</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const status = statusOf(c);
                return (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 700 }}>{c.code}</td>
                    <td style={{ color: 'var(--text-secondary)', maxWidth: 260 }}>{c.description || '—'}</td>
                    <td>{formatDiscount(c)}</td>
                    <td>{c.minimumOrderAmount ? `₹${c.minimumOrderAmount}` : '—'}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>{formatDate(c.expiresAt)}</td>
                    <td>
                      <span className={`badge ${status.toLowerCase()}`}>{status}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          title={c.isActive ? 'Deactivate' : 'Activate'}
                          onClick={() => handleToggleStatus(c)}
                          disabled={togglingId === c.id}
                        >
                          {c.isActive
                            ? <ToggleRight size={14} style={{ color: 'var(--green)' }} />
                            : <ToggleLeft size={14} style={{ color: 'var(--text-muted)' }} />}
                        </button>
                        <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setModalState({ mode: 'edit', coupon: c })} title="Edit">
                          <Edit2 size={14} />
                        </button>
                        <button className="btn btn-danger btn-icon btn-sm" onClick={() => setModalState({ mode: 'delete', coupon: c })} title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {filtered.length > 0 && (
        <div style={{ marginTop: 16, fontSize: 13, color: 'var(--text-muted)' }}>
          Showing {filtered.length} of {coupons.length} coupons
        </div>
      )}

      {(modalState?.mode === 'create' || modalState?.mode === 'edit') && (
        <CouponModal
          coupon={modalState.mode === 'edit' ? modalState.coupon : null}
          existingCoupons={coupons}
          onSave={handleSave}
          onClose={() => setModalState(null)}
          loading={actionLoading}
        />
      )}
      {modalState?.mode === 'notify' && (
        <NotificationModal
          onSend={handleSendNotification}
          onClose={() => setModalState(null)}
          loading={actionLoading}
        />
      )}
      {modalState?.mode === 'delete' && (
        <DeleteModal
          coupon={modalState.coupon}
          onConfirm={handleDelete}
          onClose={() => setModalState(null)}
          loading={actionLoading}
        />
      )}
    </Layout>
  );
}